// ZiChatExpanded — Full-featured Omega ZI AI chat.
// Features: streaming SSE · inline charts · voice input (Whisper STT) ·
//           TTS audio playback · follow-up pills · starter prompts ·
//           rich omega context (ZBucks, broker, OCI, kill switches, Greeks)
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { hapticTap, hapticSuccess, hapticMedium } from "../../utils/haptics";
import OmegaRichMessage from "../OmegaRichMessage";

const API    = `${process.env.REACT_APP_BACKEND_URL}/api`;
const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/omega/v2`;

// The axios calls in this file ride App.js's global interceptor (adds
// credentials + CSRF automatically), but the raw fetch() calls below
// (settings load, TTS, streaming chat — axios can't handle streaming
// responses) were missing credentials:"include" entirely, so no auth cookie
// was ever sent cross-origin — all 401'd. CSRF token: login/auth-me
// responses hand it back in the JSON body, stashed in localStorage under
// this key by App.js.
function csrfHeaders() {
  try {
    const stored = localStorage.getItem("roi_csrf_token");
    if (stored) return { "X-CSRF-Token": stored };
  } catch {}
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
}

// ─── Content parsers ───────────────────────────────────────────────────────
const CHART_RE    = /```chart\s*\n([\s\S]*?)\n?```/g;
const FOLLOWUPS_RE = /```followups\s*\n([\s\S]*?)\n?```/g;

function parseAssistantContent(content) {
  if (!content) return { text: "", charts: [], followups: [] };
  const charts = [], followups = [];
  let text = content, i = 0;

  text = text.replace(CHART_RE, (_full, body) => {
    try {
      charts.push({ id: `ch${i++}`, spec: JSON.parse(body.trim()), raw: body });
      return `\n[CHART::${charts.length - 1}]\n`;
    } catch { return ""; }
  });

  text = text.replace(FOLLOWUPS_RE, (_full, body) => {
    try {
      const arr = JSON.parse(body.trim());
      if (Array.isArray(arr)) arr.slice(0, 3).forEach(s => { if (typeof s === "string" && s.trim()) followups.push(s.trim()); });
    } catch {}
    return "";
  });

  return { text: text.trim(), charts, followups };
}


// ─── "Show context Omega used" expandable panel (iter 128) ─────────────────
function ContextPanel({ summary, accent, idx }) {
  const [open, setOpen] = useState(false);
  const e = summary?.engines || {};
  // Build a compact chip list
  const chips = [];
  if (e.account) chips.push("Account");
  if (typeof e.positions === "number" && e.positions > 0) chips.push(`${e.positions} positions`);
  if (typeof e.market_quotes === "number" && e.market_quotes > 0) chips.push(`${e.market_quotes} quotes`);
  if (e.regime && e.regime !== "UNKNOWN") chips.push(`Regime: ${e.regime}`);
  if (typeof e.notifications === "number" && e.notifications > 0) chips.push(`${e.notifications} alerts`);
  if (typeof e.forecasts === "number" && e.forecasts > 0) chips.push(`${e.forecasts} forecasts`);
  if (typeof e.trade_history === "number" && e.trade_history > 0) chips.push(`${e.trade_history} prior trades`);
  if (Array.isArray(e.ticker_lookup) && e.ticker_lookup.length) chips.push(`Looked up: ${e.ticker_lookup.join(", ")}`);
  if (e.kill_switches_active) chips.push("⚠ Kill switches active");
  if (e.market_open === false) chips.push("Market closed");
  if (e.recovery_mode) chips.push("Recovery mode");
  if (!chips.length) chips.push("Base context only");
  return (
    <div style={{ marginTop: 4, marginLeft: 4 }}>
      <button
        data-testid={`zi-chat-ctx-toggle-${idx}`}
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none", border: "none", color: "var(--dim)",
          fontSize: 10, fontWeight: 600, cursor: "pointer", padding: "2px 0",
          display: "inline-flex", alignItems: "center", gap: 4,
          letterSpacing: 0.3,
        }}
        title={open ? "Hide context Omega used" : "Show context Omega used"}
      >
        <span style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 120ms" }}>▸</span>
        {open ? "Hide" : "Show"} context Omega used
      </button>
      {open && (
        <div
          data-testid={`zi-chat-ctx-body-${idx}`}
          style={{
            marginTop: 6, padding: "8px 10px", borderRadius: 8,
            background: `${accent}0c`, border: `1px solid ${accent}22`,
            display: "flex", flexWrap: "wrap", gap: 5,
          }}
        >
          {chips.map((c, j) => (
            <span key={j} style={{
              fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 999,
              background: `${accent}1a`, color: "var(--text)", border: `1px solid ${accent}33`,
              whiteSpace: "nowrap",
            }}>{c}</span>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Starter prompts ────────────────────────────────────────────────────────
const STARTERS = [
  { label: "What's my edge today?",   text: "Look at my live portfolio, win rate, ZBucks readiness, and current market regime. What's my biggest edge RIGHT NOW and what single trade would maximize expected value? Plot my equity curve." },
  { label: "Risk check my book",      text: "Review my open positions and Greeks exposure. Where am I most fragile — theta bleed, vega, correlation, drawdown? Are any kill switches close to triggering? Chart the Greeks by position." },
  { label: "ZBucks status",           text: "What's my ZBucks paper trading balance, readiness score, and mode? How close am I to qualifying for real trading? What do I need to improve?" },
  { label: "Find me a setup",         text: "Scan the elite universe and surface ONE contract with the best risk/reward right now. Include entry, stop, target, PoP, and why it fits the current regime and OCI state. Plot the payoff diagram." },
];

// ─── Ticker detection ──────────────────────────────────────────────────────
const SKIP_WORDS = new Set(["A","I","AT","BE","TO","IN","ON","OR","IF","IT","IS","AS","AN","MY","WE","DO","SO","NO","UP","AM","GO","OF","BY","OK","VS","ETF","THE","AND","FOR","BUY","PUT","CALL","FROM","THAT","WITH","THIS","WHAT","WHEN","HAVE","HOLD","SELL","STOP","OPEN","LONG","INTO","SAID","HIGH","HITS","BEST","WILL","FELL","GOOD","GAVE"]);
function extractTickers(text) {
  const seen = new Set();
  const out  = [];
  // $AAPL style
  for (const m of text.matchAll(/\$([A-Z]{1,5})\b/g)) {
    const t = m[1];
    if (!seen.has(t)) { seen.add(t); out.push(t); }
  }
  // Bare uppercase 2-5 letters not in skip list
  for (const m of text.matchAll(/\b([A-Z]{2,5})\b/g)) {
    const t = m[1];
    if (!seen.has(t) && !SKIP_WORDS.has(t)) { seen.add(t); out.push(t); }
  }
  return out.slice(0, 6);
}

// ─── Full-app context fetcher (runs once at mount, refreshes every 30s) ────
async function fetchFullContext() {
  // Hard cap each call at 6s — one slow endpoint must never freeze the whole load
  const T = 6000;
  const settle = (p) => p.then(r => r.data).catch(() => null);
  const get = (url) => settle(axios.get(url, { timeout: T }));
  const [preload, zbucks, broker, oci, readiness, flowStats, riskDash, paperStats, topPicks, flowTape, acctHistory, pdtStatus, capitalCtx, zbucksFloor] = await Promise.all([
    get(`${API}/preload`),
    get(`${API_V2}/zbucks/dashboard/default`),
    get(`${API_V2}/broker/status`),
    get(`${API_V2}/oci/status`),
    get(`${API_V2}/zbucks/readiness/default`),
    get(`${API}/flow/stats`),
    get(`${API}/risk/dashboard`),
    get(`${API}/autopilot/paper-stats`),
    get(`${API}/scanner/top-picks?limit=5`),
    get(`${API}/flow/tape`),
    get(`${API}/portfolio/history?page_size=50`),
    get(`${API}/pdt/status`),
    get(`${API}/floor-chat/capital`),
    get(`${API}/floor-chat/agents/zbucks`),
  ]);

  const portfolio  = preload?.portfolio || {};
  // iter 143: /api/preload.openPositions is now `{count, positions, ...}` so
  // unwrap `.positions`. Falls back to the legacy list shape for safety.
  const opRaw = preload?.openPositions;
  const opList = Array.isArray(opRaw) ? opRaw : (opRaw?.positions || []);
  const positions  = portfolio?.positions || opList || [];
  const regimeData = preload?.regime || {};
  const omega      = preload?.omega || {};

  // TEA scores for held positions (fire-and-forget, best-effort)
  const posSymbols = positions.slice(0, 6).map(p => p.symbol).filter(Boolean);
  const teaMap = {};
  if (posSymbols.length) {
    await Promise.all(posSymbols.map(async sym => {
      try {
        const r = await axios.get(`${API}/tea/score?symbol=${sym}`, { timeout: 3000 });
        teaMap[sym] = r.data;
      } catch {}
    }));
  }

  return {
    account: portfolio,
    positions,
    market_rows: Object.values(preload?.quotes || {}),
    regime: regimeData?.regime || "TRANSITION",
    daily_pnl: preload?.dailyPnl || {},
    notifications: preload?.notifications || [],
    recent_orders: preload?.recentOrders || [],
    activity_tape: preload?.activityTape || [],
    forecasts: preload?.forecasts || [],
    open_positions: opList,
    omega_context: {
      kill_switches: omega?.killSwitches || {},
      capital_tier: omega?.capitalTier || {},
      autonomy_level: omega?.autonomyLevel || "pro",
      drift: omega?.drift || {},
      market_open: omega?.marketOpen,
      recovery_mode: omega?.recoveryMode,
      zbucks_dashboard: zbucks || {},
      broker_status: broker || {},
      oci_status: oci || {},
      zbucks_readiness: readiness || {},
      flow_stats: flowStats || {},
      risk_dashboard: riskDash || {},
      paper_stats: paperStats || {},
      scanner_picks: topPicks?.picks || [],
      flow_tape: (Array.isArray(flowTape) ? flowTape : []).slice(0, 15),
      position_tea: teaMap,
      pdt_status: pdtStatus || {},
    },
    // Keep brokerage transactions, deposits/capital, and ZBucks as separate
    // source-of-truth ledgers. The backend labels the real-USD/ZBuck wall.
    account_history: acctHistory
      ? { ...acctHistory, transactions: (acctHistory.transactions || []).slice(0, 50) }
      : { transactions: [], count: 0, totalIn: 0, totalOut: 0, net: 0 },
    capital_context: capitalCtx || {},
    zbucks_floor: zbucksFloor || {},
  };
}

// ─── Options keyword detection ─────────────────────────────────────────────
const OPTIONS_RE = /\b(option|options|chain|call|calls|put|puts|strike|expir|greeks|delta|gamma|theta|vega|iv|implied vol|spread|straddle|iron condor|covered|dte|osi)\b/i;
// OSI symbol: AAPL260117C00230000
const OSI_RE = /\b([A-Z]{1,6}\d{6}[CP]\d{8})\b/g;

// ─── Per-message ticker lookup ─────────────────────────────────────────────
async function lookupTickers(userText) {
  const upper = userText.toUpperCase();
  const tickers = extractTickers(upper);
  if (!tickers.length) return {};
  const want_options = OPTIONS_RE.test(userText);
  const osi_symbols  = [...userText.matchAll(OSI_RE)].map(m => m[1]);
  try {
    const r = await axios.post(`${API}/zi/lookup`, { tickers, want_options, osi_symbols });
    return r.data || {};
  } catch { return {}; }
}

// ─── Load persona from localStorage (written by OmegaSettingsPanel) ────────
function loadPersona() {
  try {
    const raw = localStorage.getItem("zi_ai_persona");
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

const DEFAULT_PERSONA = {
  name: "Omega",
  greeting: "Hey — I'm **Omega**, your AI trading assistant. I have live access to your portfolio, market prices, options chains, Greeks, news, flow, and every Omega engine. Ask me anything.",
  avatar_color: "#a78bfa",
  avatar_emoji: "⚡",
  risk_appetite: "BALANCED",
  tts_voice: "nova",
  personality: "FRIENDLY_TEXT",
  depth: "NORMAL",
  voice: "NATURAL",
};

// ─── Main component ─────────────────────────────────────────────────────────
export function ZiChatExpanded({ portfolioContext, regime }) {
  const [persona, setPersona] = useState(() => ({ ...DEFAULT_PERSONA, ...(loadPersona() || {}) }));

  const [messages, setMessages] = useState(() => {
    try {
      const cached = localStorage.getItem("zi_chat_msgs_v2");
      if (cached) { const p = JSON.parse(cached); if (Array.isArray(p) && p.length) return p; }
    } catch {}
    return null; // will be set after persona loads
  });

  const [input, setInput]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [recording, setRecording]   = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [autoPlay, setAutoPlay]     = useState(() => { try { return localStorage.getItem("zi_tts_auto") === "1"; } catch { return false; } });
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [convos, setConvos]           = useState([]);
  const [convosLoading, setConvosLoading] = useState(false);

  // Derive from persona — always current
  const ttsVoice  = persona.tts_voice  || "nova";
  const aiSettings = { personality: persona.personality || "FRIENDLY_TEXT", depth: persona.depth || "NORMAL", voice: persona.voice || "NATURAL" };
  const [liveCtx, setLiveCtx]       = useState(null);
  const [ctxAge, setCtxAge]         = useState(null);  // ms since last refresh
  const [ctxLoading, setCtxLoading] = useState(false);
  const [lookingUp, setLookingUp]   = useState([]);    // tickers being looked up

  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const audioRef         = useRef(null);
  const scrollRef        = useRef(null);
  const inputRef         = useRef(null);
  const liveCtxRef       = useRef(null);  // always-current ref for use inside send()
  const msgCountRef      = useRef(0);     // tracks messages.length without being a dep

  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem("zi_chat_session") || `zi-${Date.now()}`; }
    catch { return `zi-${Date.now()}`; }
  });

  useEffect(() => { try { localStorage.setItem("zi_chat_session", sessionId); } catch {} }, [sessionId]);

  // Initialize messages with persona greeting if no cache
  useEffect(() => {
    if (messages === null) {
      setMessages([{ role: "assistant", content: persona.greeting || DEFAULT_PERSONA.greeting }]);
    }
  }, [messages, persona.greeting]);

  useEffect(() => {
    if (!messages) return;
    msgCountRef.current = messages.length;
    try { localStorage.setItem("zi_chat_msgs_v2", JSON.stringify(messages.slice(-40))); } catch {}
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight + 500;
  }, [messages]);

  useEffect(() => {
    try { localStorage.setItem("zi_tts_auto", autoPlay ? "1" : "0"); } catch {}
  }, [autoPlay]);

  // Load full persona from backend + watch localStorage for live updates
  useEffect(() => {
    fetch(`${API_V2}/settings/default`, { credentials: "include", headers: csrfHeaders() }).then(r => r.json()).then(d => {
      if (d && (d.personality || d.assistant_name)) {
        const merged = { ...DEFAULT_PERSONA, ...d, name: d.assistant_name || d.name || DEFAULT_PERSONA.name, greeting: d.assistant_greeting || d.greeting || DEFAULT_PERSONA.greeting };
        setPersona(merged);
      }
    }).catch(() => {});

    const onStorage = (e) => {
      if (e.key === "zi_ai_persona" && e.newValue) {
        try { setPersona(p => ({ ...DEFAULT_PERSONA, ...p, ...JSON.parse(e.newValue) })); } catch {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Fetch full-app context on mount + every 30s — zero latency before sends
  const refreshCtx = useCallback(async () => {
    setCtxLoading(true);
    try {
      const ctx = await fetchFullContext();
      liveCtxRef.current = ctx;
      setLiveCtx(ctx);
      setCtxAge(Date.now());
    } catch {} finally { setCtxLoading(false); }
  }, []);

  useEffect(() => {
    refreshCtx();
    const id = setInterval(refreshCtx, 30000);
    return () => clearInterval(id);
  }, [refreshCtx]);

  // Load server history on mount
  useEffect(() => {
    axios.get(`${API}/zi/chat/history?session_id=${encodeURIComponent(sessionId)}&limit=40`)
      .then(r => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setMessages(prev => {
            const greet = prev[0]?.role === "assistant" ? [prev[0]] : [];
            return [...greet, ...r.data.map(m => ({ role: m.role, content: m.content }))];
          });
        }
      }).catch(() => {});
  }, [sessionId]);

  // ─── TTS ──────────────────────────────────────────────────────────────────
  const speak = useCallback(async (text, msgIdx) => {
    try {
      // strip markdown for cleaner speech
      const clean = text
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/\*(.*?)\*/g, "$1")
        .replace(/```[\s\S]*?```/g, "")
        .replace(/\[CHART::\d+\]/g, "")
        .slice(0, 4000);
      if (!clean.trim()) return;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setSpeakingIdx(msgIdx);
      const resp = await fetch(`${API}/zi/tts/speak`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({ text: clean, voice: ttsVoice }),
      });
      if (!resp.ok) throw new Error("TTS failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeakingIdx(null); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeakingIdx(null); URL.revokeObjectURL(url); };
      audio.play();
    } catch { setSpeakingIdx(null); }
  }, [ttsVoice]);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setSpeakingIdx(null);
  }, []);

  // ─── Conversation history loader (declared before `send` which depends on it) ─
  const loadConvos = useCallback(async () => {
    setConvosLoading(true);
    try {
      const r = await axios.get(`${API}/zi/chat/conversations?limit=40`);
      setConvos(Array.isArray(r.data) ? r.data : []);
    } catch {} finally { setConvosLoading(false); }
  }, []);

  // ─── Send ──────────────────────────────────────────────────────────────────
  const send = useCallback(async (overrideText) => {
    const msg = (overrideText ?? input).trim();
    if (!msg || loading) return;
    hapticTap();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);
    setLoading(true);

    let full = "";
    try {
      // Use pre-fetched context (no per-send delay). Fall back to fresh fetch if empty.
      const ctx = liveCtxRef.current || await fetchFullContext();

      // Detect tickers in user message → live lookup (price + TEA + news)
      const detectedTickers = extractTickers(msg);
      let tickerContext = {};
      if (detectedTickers.length) {
        setLookingUp(detectedTickers);
        tickerContext = await lookupTickers(msg);
        setLookingUp([]);
      }

      const resp = await fetch(`${API}/zi/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({
          message: msg,
          sessionId,
          portfolioContext: ctx.account,
          account: ctx.account,
          positions: ctx.positions,
          market_rows: ctx.market_rows,
          regime: ctx.regime,
          daily_pnl: ctx.daily_pnl,
          notifications: ctx.notifications,
          recent_orders: ctx.recent_orders,
          activity_tape: ctx.activity_tape,
          forecasts: ctx.forecasts,
          open_positions: ctx.open_positions,
          account_history: ctx.account_history,
          capital_context: ctx.capital_context,
          zbucks_floor: ctx.zbucks_floor,
          omega_context: ctx.omega_context,
          ticker_context: tickerContext,
          ai_personality: aiSettings.personality,
          ai_depth: aiSettings.depth,
          ai_voice: aiSettings.voice,
          ai_name: persona.name || "Omega",
          ai_risk_appetite: persona.risk_appetite || "BALANCED",
        }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(ln.slice(6));
            if (data.chunk) {
              full += data.chunk;
              setMessages(prev => { const a = [...prev]; a[a.length - 1] = { role: "assistant", content: full, streaming: true }; return a; });
            }
            if (data.done) {
              hapticSuccess();
              const ctxSum = data.context_summary || null;
              setMessages(prev => { const a = [...prev]; a[a.length - 1] = { role: "assistant", content: full, context_summary: ctxSum }; return a; });
              if (autoPlay) speak(full, msgCountRef.current - 1);
              // Refresh conversations list so the new auto-title appears in the sidebar
              setTimeout(() => { try { loadConvos(); } catch {} }, 1500);
            }
            if (data.error) {
              setMessages(prev => { const a = [...prev]; a[a.length - 1] = { role: "assistant", content: `_Error: ${data.error}_` }; return a; });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages(prev => { const a = [...prev]; a[a.length - 1] = { role: "assistant", content: `_Error: ${e.message}_` }; return a; });
    } finally {
      setLoading(false);
      setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, loading, sessionId, autoPlay, speak, loadConvos]);

  const clearChat = () => {
    if (!window.confirm("Clear this conversation?")) return;
    hapticTap();
    stopSpeaking();
    setMessages([{ role: "assistant", content: persona.greeting || DEFAULT_PERSONA.greeting }]);
    try { localStorage.removeItem("zi_chat_msgs_v2"); } catch {}
  };

  // ─── Conversation history management ──────────────────────────────────────
  const openConvo = useCallback(async (sid) => {
    stopSpeaking();
    setSessionId(sid);
    setShowHistory(false);
    setMessages(null); // triggers greeting fallback while loading
    try {
      localStorage.setItem("zi_chat_session", sid);
      localStorage.removeItem("zi_chat_msgs_v2");
      const r = await axios.get(`${API}/zi/chat/history?session_id=${encodeURIComponent(sid)}&limit=80`);
      if (Array.isArray(r.data) && r.data.length) {
        setMessages(r.data.map(m => ({ role: m.role, content: m.content })));
      } else {
        setMessages([{ role: "assistant", content: persona.greeting || DEFAULT_PERSONA.greeting }]);
      }
    } catch {
      setMessages([{ role: "assistant", content: persona.greeting || DEFAULT_PERSONA.greeting }]);
    }
    hapticTap();
  }, [persona.greeting, stopSpeaking]);

  const newConvo = useCallback(() => {
    const sid = `zi-${Date.now()}`;
    stopSpeaking();
    setSessionId(sid);
    setMessages([{ role: "assistant", content: persona.greeting || DEFAULT_PERSONA.greeting }]);
    setShowHistory(false);
    try { localStorage.setItem("zi_chat_session", sid); localStorage.removeItem("zi_chat_msgs_v2"); } catch {}
    hapticTap();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [persona.greeting, stopSpeaking]);

  const deleteConvo = useCallback(async (e, sid) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await axios.delete(`${API}/zi/chat/conversations/${encodeURIComponent(sid)}`);
      setConvos(prev => prev.filter(c => c.session_id !== sid));
      if (sid === sessionId) newConvo();
    } catch {}
  }, [sessionId, newConvo]);

  // ─── Mic / Whisper STT ─────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    if (recording || transcribing || loading) return;
    if (!navigator.mediaDevices || !window.MediaRecorder) { alert("Mic not supported on this browser."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 300) { setRecording(false); return; }
        setRecording(false); setTranscribing(true);
        try {
          const fd = new FormData();
          const ext = (mime || "").includes("mp4") ? "m4a" : "webm";
          fd.append("audio", blob, `voice.${ext}`);
          const r = await axios.post(`${API}/zi/stt/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 });
          const t = (r.data?.text || "").trim();
          if (t) { hapticMedium(); send(t); }
          else alert("Didn't catch that. Try again.");
        } catch (e) {
          alert(`Transcription failed: ${e?.response?.data?.detail || e.message}`);
        } finally { setTranscribing(false); }
      };
      mediaRecorderRef.current = mr; mr.start(); setRecording(true); hapticMedium();
    } catch (e) { alert(`Mic access denied: ${e.message}`); }
  }, [recording, transcribing, loading, send]);

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") try { mr.stop(); } catch {}
  }, []);

  // ─── Follow-up pills ────────────────────────────────────────────────────────
  const lastFollowups = useMemo(() => {
    if (!messages) return [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "assistant" && !m.streaming && m.content) {
        const { followups } = parseAssistantContent(m.content);
        if (followups.length) return followups;
        break;
      }
    }
    return [];
  }, [messages]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div data-testid="zi-chat-expanded" style={{ display: "flex", flexDirection: "column", minHeight: "calc(100vh - 120px)", color: "var(--text)" }}>

      {/* ── Conversation history drawer ───────────────────────────────────── */}
      {showHistory && (
        <div data-testid="zi-chat-history-overlay" style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }} onClick={() => setShowHistory(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: 320, maxWidth: "90vw", height: "100%", background: "var(--layer1)", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", boxShadow: "4px 0 32px rgba(0,0,0,0.4)", position: "relative", zIndex: 10000 }}>
            {/* Drawer header */}
            <div style={{ padding: "18px 16px 14px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: persona.avatar_color || "#a78bfa", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>{persona.avatar_emoji || "⚡"}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text)" }}>Conversations</div>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>{convos.length} total</div>
              </div>
              <button onClick={newConvo} style={{ background: persona.avatar_color || "#a78bfa", border: "none", color: "#fff", fontSize: 11, fontWeight: 800, padding: "7px 12px", borderRadius: 9, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New
              </button>
            </div>

            {/* Conversation list */}
            <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
              {convosLoading && <div style={{ padding: "24px 16px", textAlign: "center", fontSize: 12, color: "var(--muted)" }}>Loading…</div>}
              {!convosLoading && convos.length === 0 && (
                <div style={{ padding: "32px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{persona.avatar_emoji || "⚡"}</div>
                  <div style={{ fontSize: 13, color: "var(--muted)" }}>No saved conversations yet.</div>
                </div>
              )}
              {convos.map(c => {
                const isActive = c.session_id === sessionId;
                const ts = c.last_ts ? new Date(c.last_ts) : null;
                const tsLabel = ts ? ts.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
                return (
                  <div key={c.session_id} onClick={() => openConvo(c.session_id)} style={{
                    padding: "12px 16px", cursor: "pointer", borderLeft: isActive ? `3px solid ${persona.avatar_color || "#a78bfa"}` : "3px solid transparent",
                    background: isActive ? `${persona.avatar_color || "#a78bfa"}12` : "transparent",
                    display: "flex", flexDirection: "column", gap: 4, position: "relative",
                    transition: "background 0.15s",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: isActive ? (persona.avatar_color || "#a78bfa") : "var(--text)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} data-testid={`convo-title-${c.session_id}`}>
                        {c.title || c.preview || "New conversation"}
                      </div>
                      <button onClick={e => deleteConvo(e, c.session_id)} style={{ opacity: 0.4, background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 14, padding: "0 2px", flexShrink: 0 }} title="Delete">×</button>
                    </div>
                    {c.title && (
                      <div style={{ fontSize: 10, color: "var(--dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.preview}
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 10, color: "var(--dim)" }}>{tsLabel}</span>
                      <span style={{ fontSize: 10, color: "var(--dim)" }}>·</span>
                      <span style={{ fontSize: 10, color: "var(--dim)" }}>{c.msg_count} messages</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Click-outside overlay */}
          <div style={{ flex: 1 }} />
        </div>
      )}

      {/* Hero strip */}
      <div style={{ marginBottom: 14, padding: "16px 18px", borderRadius: 18, background: "linear-gradient(140deg, #0f172a 0%, #1e3a8a 60%, #4c1d95 100%)", boxShadow: "0 16px 40px rgba(79,70,229,0.25)", color: "#fff", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: `radial-gradient(circle, ${persona.avatar_color || "#a78bfa"}55, transparent 70%)`, pointerEvents: "none" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 42, height: 42, borderRadius: 13, background: persona.avatar_color || "#a78bfa", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, boxShadow: `0 6px 18px ${persona.avatar_color || "#a78bfa"}66`, flexShrink: 0 }}>
            {persona.avatar_emoji || "⚡"}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>AI ASSISTANT · LIVE</div>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 1 }}>{persona.name || "Omega"}</div>
          </div>
          {/* Auto-play TTS toggle */}
          <button data-testid="zi-chat-autoplay-toggle" onClick={() => setAutoPlay(p => !p)} title={autoPlay ? "Auto-play on (click to disable)" : "Auto-play off (click to enable)"} style={{ background: autoPlay ? "rgba(74,222,128,0.2)" : "rgba(255,255,255,0.08)", border: `1px solid ${autoPlay ? "rgba(74,222,128,0.4)" : "rgba(255,255,255,0.15)"}`, color: autoPlay ? "#4ade80" : "rgba(255,255,255,0.6)", fontSize: 11, fontWeight: 700, padding: "6px 10px", borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
            {autoPlay ? "ON" : "OFF"}
          </button>
          {/* History button */}
          <button onClick={() => { setShowHistory(h => !h); if (!showHistory) loadConvos(); }} title="Conversation history" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "7px 10px", borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="10"/></svg>
          </button>
          {/* New chat button */}
          <button onClick={newConvo} title="New conversation" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "7px 10px", borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
          <button data-testid="zi-chat-clear" onClick={clearChat} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "7px 10px", borderRadius: 10, cursor: "pointer" }}>CLEAR</button>
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 8, lineHeight: 1.5 }}>
          <span>Sonnet 4.6 · voice · TTS ({ttsVoice}) · charts · full app context · {aiSettings.personality.replace(/_/g," ")} · {aiSettings.depth}</span>
          {liveCtx && (
            <span style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
              {liveCtx.regime && <span style={{ padding: "2px 7px", borderRadius: 6, background: "rgba(167,139,250,0.25)", border: "1px solid rgba(167,139,250,0.4)", fontSize: 10, fontWeight: 700 }}>{String(liveCtx.regime).replace("_", " ")}</span>}
              {liveCtx.positions?.length > 0 && <span style={{ padding: "2px 7px", borderRadius: 6, background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.3)", fontSize: 10, fontWeight: 700 }}>{liveCtx.positions.length} pos</span>}
              {liveCtx.market_rows?.length > 0 && <span style={{ padding: "2px 7px", borderRadius: 6, background: "rgba(56,189,248,0.15)", border: "1px solid rgba(56,189,248,0.3)", fontSize: 10, fontWeight: 700 }}>{liveCtx.market_rows.length} quotes</span>}
              {liveCtx.omega_context?.kill_switches?.anyFired && <span style={{ padding: "2px 7px", borderRadius: 6, background: "rgba(239,68,68,0.2)", border: "1px solid rgba(239,68,68,0.4)", fontSize: 10, fontWeight: 700, color: "#f87171" }}>⚠ KS FIRED</span>}
              {liveCtx.omega_context?.market_open === false && <span style={{ padding: "2px 7px", borderRadius: 6, background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.3)", fontSize: 10, fontWeight: 700, color: "#fbbf24" }}>MKT CLOSED</span>}
              <button onClick={refreshCtx} disabled={ctxLoading} title="Refresh live context" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.7)", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                {ctxLoading ? "…" : "↻"} REFRESH
              </button>
            </span>
          )}
          {!liveCtx && ctxLoading && <span style={{ marginLeft: 8, opacity: 0.7 }}>Loading context…</span>}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} data-testid="zi-chat-messages" style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, paddingBottom: 10 }}>
        {(messages || []).map((m, i) => {
          const parsed = m.role === "assistant" ? parseAssistantContent(m.content || "") : { text: m.content || "", charts: [], followups: [] };
          const isSpeaking = speakingIdx === i;
          return (
            <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 6 }}>
              {/* TTS speaker button for AI messages */}
              {m.role === "assistant" && !m.streaming && m.content && (
                <button
                  data-testid={`zi-chat-speak-${i}`}
                  onClick={() => isSpeaking ? stopSpeaking() : speak(parsed.text, i)}
                  title={isSpeaking ? "Stop" : "Read aloud"}
                  style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 8, border: "1px solid var(--border)", background: isSpeaking ? "rgba(74,222,128,0.15)" : "var(--layer2)", color: isSpeaking ? "#4ade80" : "var(--dim)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 2 }}
                >
                  {isSpeaking
                    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                  }
                </button>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: "86%" }}>
                {m.role === "assistant" && <div style={{ fontSize: 10, fontWeight: 700, color: persona.avatar_color || "#a78bfa", marginLeft: 4 }}>{persona.name || "Omega"}</div>}
                <div data-testid={`zi-chat-msg-${m.role}`} style={{ padding: "10px 14px", borderRadius: m.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: m.role === "user" ? "linear-gradient(135deg, #2563eb, #3b82f6)" : "var(--layer2)", border: m.role === "user" ? "1px solid rgba(96,165,250,0.5)" : `1px solid ${persona.avatar_color || "#a78bfa"}33`, color: m.role === "user" ? "#fff" : "var(--text)", fontSize: 14, lineHeight: 1.55, wordBreak: "break-word", boxShadow: m.role === "user" ? "0 4px 14px rgba(37,99,235,0.25)" : `0 2px 12px ${persona.avatar_color || "#a78bfa"}11`, whiteSpace: m.role === "user" ? "pre-wrap" : undefined }}>
                  {m.role === "assistant"
                    ? <OmegaRichMessage content={parsed.text || (m.streaming ? "…" : "")} charts={parsed.charts} />
                    : (parsed.text || "")
                  }
                  {m.streaming && <span style={{ display: "inline-block", width: 7, height: 13, marginLeft: 3, background: "var(--muted)", verticalAlign: "middle", animation: "ziBlink 1.1s infinite" }} />}
                </div>
                {/* Context-used panel (iter 128 — trust & debugging) */}
                {m.role === "assistant" && !m.streaming && m.context_summary?.engines && (
                  <ContextPanel summary={m.context_summary} accent={persona.avatar_color || "#a78bfa"} idx={i} />
                )}
              </div>
            </div>
          );
        })}

        {/* Ticker lookup indicator */}
        {lookingUp.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 12px", borderRadius: 10, background: "rgba(56,189,248,0.08)", border: "1px solid rgba(56,189,248,0.2)", fontSize: 11, color: "#38bdf8", alignSelf: "flex-start" }}>
            <span style={{ width: 10, height: 10, border: "2px solid rgba(56,189,248,0.3)", borderTopColor: "#38bdf8", borderRadius: "50%", display: "inline-block", animation: "ziSpin 0.8s linear infinite" }} />
            Looking up {lookingUp.join(", ")} — price · TEA · news…
          </div>
        )}

        {/* Starter prompts */}
        {(messages || []).length <= 1 && (
          <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {STARTERS.map((p, i) => (
              <button key={i} data-testid={`zi-chat-starter-${i}`} onClick={() => send(p.text)} disabled={loading} style={{ textAlign: "left", padding: "10px 12px", borderRadius: 14, background: "rgba(96,165,250,0.06)", border: "1px solid rgba(96,165,250,0.2)", color: "var(--text)", fontSize: 12, fontWeight: 600, cursor: loading ? "default" : "pointer", opacity: loading ? 0.5 : 1, lineHeight: 1.35 }}>{p.label}</button>
            ))}
          </div>
        )}

        {/* Follow-up pills */}
        {!loading && lastFollowups.length > 0 && (
          <div data-testid="zi-chat-followups" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2, paddingLeft: 40 }}>
            {lastFollowups.map((f, i) => (
              <button key={i} data-testid={`zi-chat-followup-${i}`} onClick={() => send(f)} style={{ padding: "6px 12px", borderRadius: 999, background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.32)", color: "#c4b5fd", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{f}</button>
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <div style={{ position: "sticky", bottom: 0, paddingTop: 10, background: "linear-gradient(to top, var(--bg) 60%, transparent)", display: "flex", alignItems: "flex-end", gap: 8 }}>
        {/* Mic button */}
        <button data-testid="zi-chat-mic" onPointerDown={startRecording} onPointerUp={stopRecording} onPointerLeave={recording ? stopRecording : undefined} disabled={loading || transcribing} title={recording ? "Release to send" : "Hold to speak"} style={{ width: 46, height: 46, borderRadius: 14, border: "1px solid var(--border)", background: recording ? "linear-gradient(135deg, #ef4444, #f87171)" : transcribing ? "rgba(167,139,250,0.2)" : "var(--layer2)", color: recording ? "#fff" : transcribing ? "#c4b5fd" : "var(--text)", cursor: (loading || transcribing) ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: recording ? "0 0 0 4px rgba(239,68,68,0.25)" : "none", touchAction: "none", WebkitTapHighlightColor: "transparent", flexShrink: 0 }}>
          {transcribing
            ? <span style={{ width: 16, height: 16, border: "2px solid rgba(196,181,253,0.3)", borderTopColor: "#c4b5fd", borderRadius: "50%", display: "inline-block", animation: "ziSpin 0.8s linear infinite" }} />
            : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
          }
        </button>

        <textarea ref={inputRef} data-testid="zi-chat-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder={loading ? `${persona.name || "Omega"} is thinking…` : recording ? "Listening…" : transcribing ? "Transcribing…" : `Ask ${persona.name || "Omega"} anything…`} disabled={loading || recording || transcribing} style={{ flex: 1, resize: "none", minHeight: 46, maxHeight: 140, padding: "12px 14px", borderRadius: 16, background: "var(--layer2)", border: "1px solid var(--border)", color: "var(--text)", fontSize: 14, lineHeight: 1.4, outline: "none", fontFamily: "inherit" }} />

        {/* Send button */}
        <button data-testid="zi-chat-send" onClick={() => send()} disabled={!input.trim() || loading || recording || transcribing} style={{ width: 46, height: 46, borderRadius: 14, border: "none", background: (!input.trim() || loading || recording || transcribing) ? "var(--layer2)" : "linear-gradient(135deg, #38bdf8, #a78bfa)", color: (!input.trim() || loading || recording || transcribing) ? "var(--dim)" : "#0f172a", fontSize: 18, fontWeight: 900, cursor: (!input.trim() || loading || recording || transcribing) ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: (!input.trim() || loading || recording || transcribing) ? "none" : "0 6px 18px rgba(167,139,250,0.35)", flexShrink: 0 }}>
          {loading
            ? <span style={{ width: 16, height: 16, border: "2px solid rgba(15,23,42,0.3)", borderTopColor: "#0f172a", borderRadius: "50%", display: "inline-block", animation: "ziSpin 0.8s linear infinite" }} />
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          }
        </button>
      </div>

      <style>{`
        @keyframes ziBlink { 0%, 100% { opacity: 0.25; } 50% { opacity: 1; } }
        @keyframes ziSpin  { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default ZiChatExpanded;
